import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import IconButton from "@mui/material/IconButton";

interface HubspotField {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  required: boolean;
  placeholder: string;
  defaultValue: string;
}

interface HubspotFormGroup {
  fields: HubspotField[];
}

interface HubspotForm {
  guid: string;
  portalId: number;
  name: string;
  formFieldGroups: HubspotFormGroup[];
  submitText?: string;
  formType?: string;
  createdAt?: number | string;
}

interface HubspotFormPreviewProps {
  open: boolean;
  onClose: () => void;
  form: HubspotForm | null;
}

/** Count total fields */
const countFields = (form: HubspotForm | null): number => {
  if (!form?.formFieldGroups) return 0;
  return form.formFieldGroups.reduce(
    (sum, g) => sum + (g.fields?.length || 0),
    0
  );
};

const HubspotFormPreview: React.FC<HubspotFormPreviewProps> = ({
  open,
  onClose,
  form,
}) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [copyToast, setCopyToast] = useState(false);

  const shareLink = form
    ? `https://share.hsforms.com/${form.portalId}/${form.guid}`
    : "";

  useEffect(() => {
    if (!open || !form) return;

    // Load the HubSpot forms embed script if not already loaded
    const loadHubSpotScript = (): Promise<void> => {
      return new Promise((resolve) => {
        if ((window as any).hbspt) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "//js.hsforms.net/forms/embed/v2.js";
        script.charset = "utf-8";
        script.async = true;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    };

    const renderForm = async () => {
      await loadHubSpotScript();

      // Clear any previously rendered form
      if (formContainerRef.current) {
        formContainerRef.current.innerHTML = "";
      }

      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if ((window as any).hbspt && formContainerRef.current) {
          (window as any).hbspt.forms.create({
            portalId: form.portalId,
            formId: form.guid,
            target: `#hubspot-form-container`,
          });
        }
      }, 100);
    };

    renderForm();
  }, [open, form]);

  if (!form) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopyToast(true);
  };

  const fieldCount = countFields(form);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            overflow: "hidden",
          },
        }}
      >
        {/* ─── Header ─── */}
        <DialogTitle
          sx={{
            bgcolor: "#faf8f3",
            borderBottom: "1px solid #f0ece3",
            py: 2,
            px: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: "16px", lineHeight: 1.4, color: "#212121" }}
              >
                {form.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} mt={0.5}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", color: "#888" }}
                >
                  {form.formType || "Standard"} • {fieldCount} field{fieldCount !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: "#999", mt: 0.25, "&:hover": { bgcolor: "#f0ece3" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {/* ─── Share Link ─── */}
          <Box mb={2.5}>
            {/* <Typography
              variant="subtitle2"
              sx={{ mb: 0.75, fontWeight: 600, fontSize: "13px", color: "#333" }}
            >
              Share Link
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                p: 1.25,
                bgcolor: "#faf8f3",
                borderRadius: "6px",
                border: "1px solid #f0ece3",
              }}
            >
              <LinkIcon sx={{ color: "#999", fontSize: "16px", flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="a"
                href={shareLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#1976d2",
                  fontSize: "13px",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {shareLink}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                title="Copy share link"
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e8e4da",
                  borderRadius: "4px",
                  padding: "4px",
                  "&:hover": { bgcolor: "#f5f2ea" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: "14px", color: "#666" }} />
              </IconButton>
            </Box> */}
          </Box>

          {/* ─── Form Fields Summary ─── */}
          {fieldCount > 0 && (
            <Box mb={2.5}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.75, fontWeight: 600, fontSize: "13px", color: "#333" }}
              >
                Form Fields ({fieldCount})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.75}>
                {form.formFieldGroups
                  .flatMap((g) => g.fields || [])
                  .map((field, i) => (
                    <Chip
                      key={i}
                      label={field.label || field.name}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        borderColor: "#e0dcd4",
                        color: "#555",
                        height: "26px",
                        borderRadius: "4px",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  ))}
              </Box>
            </Box>
          )}

          {/* ─── Live Form Preview ─── */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.75, fontWeight: 600, fontSize: "13px", color: "#333" }}
            >
              Live Preview
            </Typography>
            <Box
              id="hubspot-form-container"
              ref={formContainerRef}
              sx={{
                p: 3,
                minHeight: 220,
                border: "1px solid #f0ece3",
                borderRadius: "6px",
                bgcolor: "#fff",
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #f0ece3" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: "#d0cbc0",
              color: "#555",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "13px",
              borderRadius: "6px",
              px: 2.5,
              "&:hover": { borderColor: "#b0a990", bgcolor: "#faf8f3" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy-to-clipboard toast */}
      <Snackbar
        open={copyToast}
        autoHideDuration={2000}
        onClose={() => setCopyToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopyToast(false)}
          sx={{ borderRadius: "6px" }}
        >
          Share link copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default HubspotFormPreview;
